import { expandSelectionItemsForAiContext } from './expandSelectionForAiContext.js';
import { extractTextShapeContent } from './canvasTextContent.js';
import { readArrowLabel } from './arrowTerminalLabels.js';
import { selectionItemFromShape } from './selectionItemFromShape.js';
import { readBoardGoal, readCurrentStep } from './boardGoalFields.js';

/** Keep Gemini URL + in-app selection chips small. */
export const RELATED_ASK_CONTEXT_MAX = 12;
export const RELATED_ASK_TEXT_MAX = 6;
export const RELATED_ASK_SPARSE_MAX = 8;

const PORTABLE_TYPES = new Set([
  'pin-card',
  'suggest-pin',
  'conversation-pin',
  'text',
  'note',
  'frame',
  'image',
  'geo',
]);

const STOP_WORDS = new Set([
  'the', 'and', 'for', 'are', 'but', 'not', 'you', 'your', 'this', 'that',
  'with', 'from', 'have', 'has', 'was', 'were', 'been', 'being', 'its',
  'what', 'which', 'when', 'where', 'who', 'how', 'why', 'can', 'could',
  'should', 'would', 'will', 'just', 'than', 'into', 'onto', 'about',
  'over', 'under', 'based', 'chosen', 'please', 'respond', 'question',
  'draft', 'best', 'next', 'step', 'also', 'some', 'any', 'our', 'my',
]);

const TOKEN_ALIASES = {
  trip: ['travel', 'journey', 'itinerary', 'vacation', 'holiday'],
  travel: ['trip', 'journey', 'itinerary'],
  itinerary: ['trip', 'travel', 'plan', 'schedule', 'route'],
  cruise: ['ship', 'sailing', 'boat'],
  mode: ['transport', 'flight', 'train', 'drive', 'cruise'],
  budget: ['cost', 'price', 'cheap', 'expensive'],
};

function activityIdOf(activity) {
  return String(activity?._id || activity?.actionId || '');
}

export function tokenizeAskContext(text) {
  const tokens = String(text || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .map(token => token.trim())
    .filter(token => token.length > 2 && !STOP_WORDS.has(token));
  const expanded = new Set(tokens);
  for (const token of tokens) {
    for (const alias of TOKEN_ALIASES[token] || []) {
      expanded.add(alias);
    }
  }
  return [...expanded];
}

function sameTitle(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

function findBlocker(blockers = [], action = null) {
  const title = String(action?.blockerTitle || '').trim();
  if (!title) return blockers[0] || null;
  return blockers.find(item => sameTitle(item?.title, title)) || blockers[0] || null;
}

function activityHaystack(activity) {
  if (!activity) return '';
  const content = activity.content?.[0] || {};
  const url = content.urls?.[0] || {};
  const comment = content.comments?.[0] || {};
  const highlight = comment.highlightData || activity.action?.highlightData || {};
  return [
    content.title,
    url.title,
    comment.text,
    comment.body,
    highlight.intentSummary,
    highlight.selectionText,
    highlight.ocrText,
  ].filter(Boolean).join(' ');
}

function shapeHaystack(shape, activities = []) {
  if (!shape) return '';
  if (shape.type === 'pin-card') {
    const activity = (activities || []).find(
      item => activityIdOf(item) === String(shape.meta?.activityId || '')
    );
    return [
      shape.props?.title,
      activityHaystack(activity),
    ].filter(Boolean).join(' ');
  }
  if (shape.type === 'suggest-pin') {
    return [shape.props?.title, shape.props?.snippet, shape.props?.reason].filter(Boolean).join(' ');
  }
  if (shape.type === 'conversation-pin') {
    return [shape.props?.title, shape.props?.snippet].filter(Boolean).join(' ');
  }
  if (shape.type === 'text' || shape.type === 'note') {
    return extractTextShapeContent(shape);
  }
  if (shape.type === 'frame') {
    return [shape.props?.name, shape.meta?.intentSummary].filter(Boolean).join(' ');
  }
  if (shape.type === 'image') {
    return [shape.meta?.intentSummary, shape.meta?.ocrText].filter(Boolean).join(' ');
  }
  if (shape.type === 'geo') {
    return [shape.props?.name, shape.props?.text].filter(Boolean).join(' ');
  }
  if (shape.type === 'arrow') {
    return readArrowLabel(shape);
  }
  return '';
}

function scoreHaystack(haystack, tokens) {
  if (!tokens.length) return 0;
  const text = String(haystack || '').toLowerCase();
  if (!text) return 0;
  let score = 0;
  for (const token of tokens) {
    if (text.includes(token)) score += 10;
  }
  return score;
}

function isPortableShape(shape) {
  if (!shape || !PORTABLE_TYPES.has(shape.type)) return false;
  if (shape.type === 'geo') {
    return Boolean(String(shape.props?.text || shape.props?.name || '').trim());
  }
  if (shape.type === 'image') {
    return Boolean(String(shape.meta?.intentSummary || shape.meta?.ocrText || shape.props?.url || '').trim());
  }
  if (shape.type === 'text' || shape.type === 'note') {
    return Boolean(extractTextShapeContent(shape).trim());
  }
  return true;
}

function findPinShape(editor, activityId) {
  const needle = String(activityId || '');
  if (!editor || !needle) return null;
  const desired = `shape:pin-${needle}`;
  const direct = editor.getShape?.(desired);
  if (direct) return direct;
  return (editor.getCurrentPageShapes?.() || []).find(
    shape => (shape.type === 'pin-card' || shape.type === 'suggest-pin')
      && String(shape.meta?.activityId || '') === needle
  ) || null;
}

function findFrameShape(editor, clusterId) {
  const raw = String(clusterId || '');
  if (!editor || !raw.startsWith('frame:')) return null;
  const logicalId = raw.slice('frame:'.length);
  return (editor.getCurrentPageShapes?.() || []).find(
    shape => shape.type === 'frame'
      && (String(shape.meta?.boardFrameId || '') === logicalId
        || String(shape.id).includes(logicalId))
  ) || null;
}

function itemFromShape(shape, activities) {
  return selectionItemFromShape(shape, { activities });
}

function addItem(into, seen, item) {
  if (!item?.key || seen.has(item.key)) return;
  seen.add(item.key);
  into.push(item);
}

function addShape(into, seen, shape, activities) {
  if (!isPortableShape(shape)) return;
  addItem(into, seen, itemFromShape(shape, activities));
}

/**
 * Related canvas objects for a Do-next / chat ask.
 * Always keeps user notes (capped), the current selection, and coach-linked
 * pins/frames. Other pins/frames must overlap the question, step, or gap.
 * Sparse boards send every portable object.
 */
export function collectRelatedAskContextItems(
  editor,
  {
    activities = [],
    question = '',
    action = null,
    blockers = [],
    board = null,
    maxItems = RELATED_ASK_CONTEXT_MAX,
  } = {}
) {
  if (!editor?.getCurrentPageShapes) return [];

  const queryText = [
    question,
    action?.question,
    action?.label,
    action?.blockerTitle,
    readCurrentStep(board),
    readBoardGoal(board),
  ].filter(Boolean).join(' ');
  const tokens = tokenizeAskContext(queryText);
  const blocker = findBlocker(blockers, action);
  const must = [];
  const seen = new Set();

  for (const shapeId of editor.getSelectedShapeIds?.() || []) {
    addShape(must, seen, editor.getShape?.(shapeId), activities);
  }

  for (const activityId of blocker?.exemplarPinIds || []) {
    addShape(must, seen, findPinShape(editor, activityId), activities);
  }
  for (const clusterId of blocker?.clusterIds || []) {
    addShape(must, seen, findFrameShape(editor, clusterId), activities);
  }

  const progress = board?.contextRecommendations?.userProgress || {};
  const blockerKey = String(blocker?.title || '').trim().toLowerCase();
  const fills = [
    ...(Array.isArray(progress.doneFills) ? progress.doneFills : []),
    ...(Array.isArray(progress.partialFills) ? progress.partialFills : []),
  ];
  for (const fill of fills) {
    const fillKey = String(fill?.doneKey || fill?.partialKey || '').trim().toLowerCase();
    if (blockerKey && fillKey && fillKey !== blockerKey) continue;
    if (fill?.activityId) addShape(must, seen, findPinShape(editor, fill.activityId), activities);
    else if (fill?.shapeId) addShape(must, seen, editor.getShape?.(fill.shapeId), activities);
  }

  const pageShapes = editor.getCurrentPageShapes() || [];
  const textShapes = pageShapes.filter(shape => (
    (shape.type === 'text' || shape.type === 'note') && isPortableShape(shape)
  ));
  const rankedNotes = textShapes
    .map(shape => ({ shape, score: scoreHaystack(shapeHaystack(shape, activities), tokens) }))
    .sort((a, b) => b.score - a.score || String(a.shape.id).localeCompare(String(b.shape.id)));
  for (const entry of rankedNotes.slice(0, RELATED_ASK_TEXT_MAX)) {
    addShape(must, seen, entry.shape, activities);
  }

  const scored = [];
  for (const shape of pageShapes) {
    if (!isPortableShape(shape)) continue;
    if (shape.type === 'text' || shape.type === 'note') continue;
    const item = itemFromShape(shape, activities);
    if (!item?.key || seen.has(item.key)) continue;
    const score = scoreHaystack(shapeHaystack(shape, activities), tokens);
    if (score > 0) scored.push({ item, score });
  }
  scored.sort((a, b) => b.score - a.score || String(a.item.key).localeCompare(String(b.item.key)));

  const portable = pageShapes.filter(isPortableShape);
  const related = [...must];
  if (portable.length <= RELATED_ASK_SPARSE_MAX) {
    for (const shape of portable) addShape(related, seen, shape, activities);
  } else {
    for (const entry of scored) addItem(related, seen, entry.item);
  }

  const expanded = expandSelectionItemsForAiContext(editor, related, { activities });
  return expanded.slice(0, Math.max(1, Number(maxItems) || RELATED_ASK_CONTEXT_MAX));
}
